import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { Button } from "src/components/ui/Button";
import { Input } from "src/components/ui/Input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/Select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/ui/Table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "src/components/ui/AlertDialog";
import {
  ArrowLeft,
  Search,
  Users,
  Edit,
  Trash2,
  Eye,
  UserPlus,
  Download,
  Copy,
  LayoutGrid,
  List,
  CheckCircle2,
  Clock,
  ShieldCheck,
  GraduationCap,
  Sparkles,
  AlertCircle,
  X,
  ExternalLink,
} from "lucide-react";
import { useToast } from "src/components/ui/UseToast";
import { useUsers, useDeleteUser, User } from "src/hooks/useUsers";
import { ROUTES } from "src/routes/RouteConfig";

export const ListUsers = () => {
  const navigate = useNavigate();
  const { setTitle, setDescription, setActions, reset } = usePageHeaderStore();
  const { toast } = useToast();

  // Filters & display state
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [viewMode, setViewMode] = useState<"card" | "table">("card");

  // Selection & pagination state
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(25);

  // Deletion modal state
  const [deleteConfirmUser, setDeleteConfirmUser] = useState<User | null>(null);

  // Queries & mutations
  const { data: usersData, isLoading, error, isError } = useUsers();
  const deleteUserMutation = useDeleteUser();

  // Top PageHeader store sync
  useEffect(() => {
    setTitle("User Directory");
    setDescription("Coordinate platform accounts, roles, curriculum authorizations, and credentials.");
    setActions(null);

    return () => reset();
  }, [setTitle, setDescription, setActions, reset]);

  // Error handling
  useEffect(() => {
    if (isError && error) {
      toast({
        title: "Error",
        description: "Failed to fetch users. Please refresh the page.",
        variant: "destructive",
      });
    }
  }, [isError, error, toast]);

  const rawUsers = usersData?.users || [];

  // KPI Metrics computation
  const metrics = useMemo(() => {
    const total = rawUsers.length;
    const tutors = rawUsers.filter((u) => u.role?.toLowerCase() === "tutor").length;
    const students = rawUsers.filter((u) => u.role?.toLowerCase() === "student").length;
    const admins = rawUsers.filter((u) => u.role?.toLowerCase() === "admin").length;
    const pendingOrAction = rawUsers.filter(
      (u) => !u.status || u.status.toLowerCase() === "pending" || u.status.toLowerCase() === "inactive"
    ).length;

    return { total, tutors, students, admins, pendingOrAction };
  }, [rawUsers]);

  // Filtered users
  const filteredUsers = useMemo(() => {
    return rawUsers.filter((user) => {
      // 1. Tab segment filter
      if (activeTab === "student" && user.role?.toLowerCase() !== "student") return false;
      if (activeTab === "tutor" && user.role?.toLowerCase() !== "tutor") return false;
      if (activeTab === "admin" && user.role?.toLowerCase() !== "admin") return false;
      if (
        activeTab === "inactive" &&
        user.status?.toLowerCase() !== "inactive" &&
        user.status?.toLowerCase() !== "suspended"
      )
        return false;

      // 2. Status dropdown filter
      if (statusFilter !== "all" && user.status?.toLowerCase() !== statusFilter.toLowerCase()) {
        return false;
      }

      // 3. Search query filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        const fullName = `${user.first_name || ""} ${user.last_name || ""}`.toLowerCase();
        const email = (user.email || "").toLowerCase();
        const id = (user.id || "").toLowerCase();

        if (!fullName.includes(query) && !email.includes(query) && !id.includes(query)) {
          return false;
        }
      }

      return true;
    });
  }, [rawUsers, activeTab, statusFilter, searchQuery]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, statusFilter, searchQuery, pageSize]);

  // Pagination calculation
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize));
  const paginatedUsers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredUsers.slice(start, start + pageSize);
  }, [filteredUsers, currentPage, pageSize]);

  // Multi-selection helpers
  const isAllSelected =
    paginatedUsers.length > 0 && paginatedUsers.every((u) => selectedUserIds.includes(u.id));

  const handleToggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedUserIds((prev) => prev.filter((id) => !paginatedUsers.some((u) => u.id === id)));
    } else {
      const pageIds = paginatedUsers.map((u) => u.id);
      setSelectedUserIds((prev) => Array.from(new Set([...prev, ...pageIds])));
    }
  };

  const handleToggleSelectUser = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  // CSV Export
  const handleExportCSV = () => {
    if (filteredUsers.length === 0) {
      toast({
        title: "Export Notice",
        description: "No user records match the active criteria.",
        variant: "default",
      });
      return;
    }

    const headers = ["ID", "First Name", "Last Name", "Email", "Role", "Status", "Created At", "Last Sign In"];
    const rows = filteredUsers.map((u) => [
      u.id,
      `"${(u.first_name || "").replace(/"/g, '""')}"`,
      `"${(u.last_name || "").replace(/"/g, '""')}"`,
      `"${(u.email || "").replace(/"/g, '""')}"`,
      u.role || "",
      u.status || "",
      u.created_at,
      u.last_sign_in_at || "",
    ]);

    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `rflearn_users_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "Export Started",
      description: `Exported ${filteredUsers.length} user records to CSV.`,
    });
  };

  // Copy selected emails
  const handleCopySelectedEmails = () => {
    const emails = rawUsers
      .filter((u) => selectedUserIds.includes(u.id))
      .map((u) => u.email)
      .filter(Boolean)
      .join(", ");

    if (!emails) return;

    navigator.clipboard.writeText(emails);
    toast({
      title: "Emails Copied",
      description: `Copied ${selectedUserIds.length} email addresses to clipboard.`,
    });
  };

  // User deletion
  const handleExecuteDelete = async () => {
    if (!deleteConfirmUser) return;

    try {
      await deleteUserMutation.mutateAsync(deleteConfirmUser.id);
      setSelectedUserIds((prev) => prev.filter((id) => id !== deleteConfirmUser.id));
      toast({
        title: "User Deleted",
        description: `Successfully removed ${deleteConfirmUser.email}.`,
      });
    } catch {
      toast({
        title: "Error",
        description: "Failed to delete user. Please try again.",
        variant: "destructive",
      });
    } finally {
      setDeleteConfirmUser(null);
    }
  };

  // Semantic Status Badges complying with rfLearn DESIGN.md
  const renderStatusBadge = (status?: string | null) => {
    const s = (status || "pending").toLowerCase();
    switch (s) {
      case "active":
        return (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Active
          </span>
        );
      case "inactive":
      case "suspended":
        return (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-slate-100 text-slate-700 border border-slate-200 inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-slate-400"></span> Inactive
          </span>
        );
      case "pending":
      default:
        return (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-50 text-amber-800 border border-amber-300 inline-flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span> Pending
          </span>
        );
    }
  };

  // Role Badges
  const renderRoleBadge = (role?: string | null) => {
    const r = (role || "student").toLowerCase();
    switch (r) {
      case "admin":
        return (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-rose-50 text-rose-700 border border-rose-300">
            Admin / Staff
          </span>
        );
      case "tutor":
        return (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200">
            Faculty Tutor
          </span>
        );
      case "student":
      default:
        return (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-50 text-amber-800 border border-amber-300">
            Student
          </span>
        );
    }
  };

  // Helper for user initials
  const getUserInitials = (user: User) => {
    const first = user.first_name ? user.first_name.charAt(0).toUpperCase() : "";
    const last = user.last_name ? user.last_name.charAt(0).toUpperCase() : "";
    if (first || last) return `${first}${last}`;
    return user.email ? user.email.substring(0, 2).toUpperCase() : "U";
  };

  // Helper for role-specific Card & Avatar styling
  const getCardTheme = (role?: string | null) => {
    const r = (role || "student").toLowerCase();
    switch (r) {
      case "admin":
        return {
          cardContainer:
            "border border-rose-200 rounded-xl p-4 bg-rose-50/25 hover:border-rose-300 hover:shadow-xs transition-all shadow-2xs flex flex-col justify-between",
          avatarBg: "bg-rose-500 text-white font-bold shadow-xs",
          innerBox: "bg-white p-2.5 rounded-lg border border-rose-100",
          actionText: "text-rose-700 hover:text-rose-900 font-bold",
          borderAccent: "border-rose-200/80",
        };
      case "tutor":
        return {
          cardContainer:
            "border border-slate-200 rounded-xl p-4 bg-white hover:border-blue-300 hover:shadow-xs transition-all shadow-2xs flex flex-col justify-between",
          avatarBg: "bg-blue-100 text-blue-700 font-bold border border-blue-200",
          innerBox: "bg-[#faf9fe] p-2.5 rounded-lg border border-slate-100",
          actionText: "text-blue-600 hover:text-blue-800 font-bold",
          borderAccent: "border-slate-100",
        };
      case "student":
      default:
        return {
          cardContainer:
            "border border-amber-300 rounded-xl p-4 bg-amber-50/25 hover:border-amber-400 hover:shadow-xs transition-all shadow-2xs flex flex-col justify-between",
          avatarBg: "bg-amber-500 text-white font-bold shadow-xs",
          innerBox: "bg-white p-2.5 rounded-lg border border-amber-200",
          actionText: "text-amber-800 hover:text-amber-950 font-bold",
          borderAccent: "border-amber-200/80",
        };
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-80 space-y-3">
        <div className="animate-spin rounded-full h-9 w-9 border-b-2 border-purple-600"></div>
        <p className="text-xs text-slate-500 font-medium">Loading user records...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      
      {/* Top Breadcrumbs */}
      <div className="flex items-center gap-1.5 text-xs text-slate-500 pb-1">
        <button
          type="button"
          onClick={() => navigate(ROUTES.ADMIN_DASHBOARD?.path || "/admin/dashboard")}
          className="hover:text-purple-700 transition"
        >
          Admin Console
        </button>
        <span>/</span>
        <button
          type="button"
          onClick={() => navigate(ROUTES.ADMIN_USERS?.path || "/admin/users")}
          className="hover:text-purple-700 transition"
        >
          User Operations
        </button>
        <span>/</span>
        <span className="font-semibold text-slate-800">User Directory</span>
      </div>

      {/* 1. Top KPI Telemetry Ribbon */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        
        {/* Total Users */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-2xs hover:border-slate-300 transition-colors">
          <div className="flex items-center justify-between text-xs font-semibold text-slate-500">
            <span>TOTAL USERS</span>
            <span className="text-emerald-600 font-mono font-medium">Directory</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{metrics.total}</div>
          <div className="text-xs text-slate-400 mt-0.5">Platform registered accounts</div>
        </div>

        {/* Active Tutors (Blue) */}
        <div className="bg-white p-4 rounded-xl border border-blue-200 shadow-2xs hover:border-blue-300 transition-colors">
          <div className="flex items-center justify-between text-xs font-semibold text-blue-700">
            <span>ACTIVE TUTORS</span>
            <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-semibold border border-blue-200">
              Faculty
            </span>
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{metrics.tutors}</div>
          <div className="text-xs text-slate-400 mt-0.5">Subject mentor roster</div>
        </div>

        {/* Enrolled Students (Amber) */}
        <div className="bg-white p-4 rounded-xl border border-amber-200 shadow-2xs hover:border-amber-300 transition-colors">
          <div className="flex items-center justify-between text-xs font-semibold text-amber-800">
            <span>STUDENTS ENROLLED</span>
            <span className="text-amber-700 font-mono font-medium">{metrics.students} Active</span>
          </div>
          <div className="text-2xl font-bold text-slate-900 mt-1">{metrics.students}</div>
          <div className="text-xs text-slate-400 mt-0.5">Learning plan subscribers</div>
        </div>

        {/* Staff / Admins (Rose) */}
        <div className="bg-white p-4 rounded-xl border border-rose-200 shadow-2xs hover:border-rose-300 transition-colors">
          <div className="flex items-center justify-between text-xs font-semibold text-rose-700">
            <span>STAFF / ADMINS</span>
            <span className="w-2 h-2 rounded-full bg-rose-500 animate-pulse"></span>
          </div>
          <div className="text-2xl font-bold text-rose-700 mt-1">{metrics.admins}</div>
          <div className="text-xs text-slate-400 mt-0.5">Console dispatchers & authors</div>
        </div>
      </div>

      {/* 2. Main Workspace Card */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden">
        
        {/* Header with Role Color Legend & View Toggle */}
        <div className="px-5 pt-4 pb-0 border-b border-slate-200 bg-[#faf9fe]">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold text-slate-900">User Records</h2>
                <span className="text-xs font-mono text-slate-400">({filteredUsers.length} records)</span>
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                Manage accounts, role assignments, and platform access.
              </p>
            </div>

            <div className="flex items-center gap-2 self-start sm:self-auto">
              
              {/* VIEW TOGGLE (Card vs Table) */}
              <div className="flex items-center border border-slate-300 rounded-lg p-0.5 bg-slate-100 shadow-2xs">
                <button
                  type="button"
                  onClick={() => setViewMode("card")}
                  className={`px-2.5 py-1.5 rounded-md font-bold text-xs flex items-center gap-1.5 transition-all ${
                    viewMode === "card"
                      ? "bg-white shadow-2xs text-purple-700"
                      : "text-slate-600 hover:text-slate-900 font-semibold"
                  }`}
                >
                  <LayoutGrid size={14} className={viewMode === "card" ? "text-purple-600" : "text-slate-500"} />
                  <span>Card View</span>
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("table")}
                  className={`px-2.5 py-1.5 rounded-md font-bold text-xs flex items-center gap-1.5 transition-all ${
                    viewMode === "table"
                      ? "bg-white shadow-2xs text-purple-700"
                      : "text-slate-600 hover:text-slate-900 font-semibold"
                  }`}
                >
                  <List size={14} className={viewMode === "table" ? "text-purple-600" : "text-slate-500"} />
                  <span>Table</span>
                </button>
              </div>

              {/* Export CSV */}
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                className="h-8 px-2.5 text-xs font-semibold text-slate-700 flex items-center gap-1.5 shadow-2xs"
              >
                <Download size={13} className="text-slate-500" />
                Export CSV
              </Button>

              {/* Add User */}
              <Button
                size="sm"
                onClick={() => navigate(ROUTES.ADMIN_USERS_CREATE?.path || "/admin/users/create")}
                className="h-8 px-3 text-xs font-semibold bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-1.5 shadow-2xs"
              >
                <UserPlus size={13} />
                Add User
              </Button>
            </div>
          </div>

          {/* Flush Segmented Tabs complying with DESIGN.md -mb-px rule */}
          <div className="flex items-center gap-6 overflow-x-auto text-xs font-semibold border-b border-transparent">
            <button
              type="button"
              onClick={() => setActiveTab("all")}
              className={`py-2.5 -mb-px flex items-center gap-1.5 border-b-2 transition-colors ${
                activeTab === "all"
                  ? "border-purple-600 text-purple-700 font-bold"
                  : "border-transparent text-slate-600 hover:text-slate-900"
              }`}
            >
              <span>All Users</span>
              <span className="px-1.5 py-0.2 rounded-full bg-purple-100 text-purple-800 text-xs font-mono">
                {rawUsers.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("student")}
              className={`py-2.5 -mb-px flex items-center gap-1.5 border-b-2 transition-colors ${
                activeTab === "student"
                  ? "border-amber-500 text-amber-800 font-bold"
                  : "border-transparent text-slate-600 hover:text-slate-900"
              }`}
            >
              <span>Students</span>
              <span className="px-1.5 py-0.2 rounded-full bg-amber-100 text-amber-800 text-xs font-mono">
                {metrics.students}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("tutor")}
              className={`py-2.5 -mb-px flex items-center gap-1.5 border-b-2 transition-colors ${
                activeTab === "tutor"
                  ? "border-blue-600 text-blue-700 font-bold"
                  : "border-transparent text-slate-600 hover:text-slate-900"
              }`}
            >
              <span>Tutors</span>
              <span className="px-1.5 py-0.2 rounded-full bg-blue-100 text-blue-800 text-xs font-mono">
                {metrics.tutors}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("admin")}
              className={`py-2.5 -mb-px flex items-center gap-1.5 border-b-2 transition-colors ${
                activeTab === "admin"
                  ? "border-rose-500 text-rose-700 font-bold"
                  : "border-transparent text-slate-600 hover:text-slate-900"
              }`}
            >
              <span>Staff / Admins</span>
              <span className="px-1.5 py-0.2 rounded-full bg-rose-100 text-rose-800 text-xs font-mono">
                {metrics.admins}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab("inactive")}
              className={`py-2.5 -mb-px flex items-center gap-1.5 border-b-2 transition-colors ${
                activeTab === "inactive"
                  ? "border-slate-600 text-slate-800 font-bold"
                  : "border-transparent text-slate-600 hover:text-slate-900"
              }`}
            >
              <span>Suspended / Inactive</span>
            </button>
          </div>
        </div>

        {/* Toolbar: Search & Filters */}
        <div className="p-3.5 bg-white border-b border-slate-200 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5 flex-1 min-w-[280px]">
            <div className="relative flex-1 max-w-md">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-2.5" />
              <Input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by name, email, or UUID..."
                className="pl-9 pr-8 h-9 text-xs rounded-lg border-slate-300 focus:ring-2 focus:ring-purple-600"
              />
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Status Dropdown */}
            <div className="w-36">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9 text-xs">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {(searchQuery || statusFilter !== "all" || activeTab !== "all") && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter("all");
                  setActiveTab("all");
                }}
                className="h-8 text-xs text-slate-600 hover:text-slate-900"
              >
                Reset Filters
              </Button>
            )}
            <span className="text-xs text-slate-400 font-mono">
              Page {currentPage} of {totalPages}
            </span>
          </div>
        </div>

        {/* Bulk Action Bar (when users are selected) */}
        {selectedUserIds.length > 0 && (
          <div className="bg-purple-50 px-4 py-2 border-b border-purple-200 flex items-center justify-between text-xs text-purple-900">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={isAllSelected}
                onChange={handleToggleSelectAll}
                className="w-3.5 h-3.5 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="font-semibold">{selectedUserIds.length} user(s) selected</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopySelectedEmails}
                className="h-7 text-xs bg-white border-purple-300 text-purple-700 hover:bg-purple-100 flex items-center gap-1"
              >
                <Copy size={12} />
                Copy Emails
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedUserIds([])}
                className="h-7 text-xs text-purple-700 hover:bg-purple-100"
              >
                Clear
              </Button>
            </div>
          </div>
        )}

        {/* ========================================================= */}
        {/* VIEW 1: CARD VIEW (Role-Colored Cards) */}
        {/* ========================================================= */}
        {viewMode === "card" && (
          <div className="p-5 bg-slate-50/50">
            {paginatedUsers.length === 0 ? (
              <div className="text-center py-12 text-slate-500 bg-white rounded-xl border border-slate-200">
                <Users className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                <div className="text-sm font-bold text-slate-800">No users found</div>
                <p className="text-xs text-slate-500 mt-1">Try adjusting your search query or tab filters.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {paginatedUsers.map((user) => {
                  const theme = getCardTheme(user.role);
                  const isSelected = selectedUserIds.includes(user.id);

                  return (
                    <div
                      key={user.id}
                      className={`${theme.cardContainer} ${
                        isSelected ? "ring-2 ring-purple-600 ring-offset-1" : ""
                      }`}
                    >
                      <div>
                        {/* Card Header */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleToggleSelectUser(user.id)}
                              className="w-3.5 h-3.5 rounded border-slate-300 text-purple-600 focus:ring-purple-500 self-start mt-1"
                            />
                            <div
                              className={`w-10 h-10 rounded-full flex items-center justify-center text-xs ${theme.avatarBg}`}
                            >
                              {getUserInitials(user)}
                            </div>
                            <div>
                              <h4 className="text-xs font-bold text-slate-900 leading-tight">
                                {`${user.first_name || ""} ${user.last_name || ""}`.trim() || "Unnamed User"}
                              </h4>
                              <div className="text-xs text-slate-500 font-mono truncate max-w-[180px]">
                                {user.email}
                              </div>
                            </div>
                          </div>
                          <div>{renderStatusBadge(user.status)}</div>
                        </div>

                        {/* Inner Details Container */}
                        <div className={`mt-3 space-y-1.5 text-xs ${theme.innerBox}`}>
                          <div className="flex justify-between items-center text-xs">
                            <span className="text-slate-500">Assigned Role:</span>
                            <span>{renderRoleBadge(user.role)}</span>
                          </div>

                          <div className="flex justify-between items-center text-xs">
                            <span className="text-slate-500">Created:</span>
                            <span className="font-mono text-slate-700">
                              {user.created_at ? new Date(user.created_at).toLocaleDateString() : "-"}
                            </span>
                          </div>

                          <div className="flex justify-between items-center text-xs">
                            <span className="text-slate-500">Last Sign In:</span>
                            <span className="font-mono text-slate-700">
                              {user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString() : "Never"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Card Footer Actions */}
                      <div className={`mt-4 pt-3 border-t ${theme.borderAccent} flex items-center justify-between text-xs`}>
                        <button
                          type="button"
                          onClick={() => navigate(`${ROUTES.ADMIN_USERS_VIEW?.path || "/admin/users/view"}?id=${user.id}`)}
                          className={`text-xs flex items-center gap-1 ${theme.actionText}`}
                        >
                          View Details &rarr;
                        </button>
                        <div className="flex items-center gap-1.5">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`${ROUTES.ADMIN_USERS_EDIT?.path || "/admin/users/edit"}?id=${user.id}`)}
                            className="h-7 w-7 p-0 text-slate-600 hover:text-slate-900"
                            title="Edit User"
                          >
                            <Edit size={13} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteConfirmUser(user)}
                            className="h-7 w-7 p-0 text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            title="Delete User"
                          >
                            <Trash2 size={13} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ========================================================= */}
        {/* VIEW 2: TABULAR VIEW (High-Density Table) */}
        {/* ========================================================= */}
        {viewMode === "table" && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  <TableHead className="w-10">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={handleToggleSelectAll}
                      className="w-3.5 h-3.5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                    />
                  </TableHead>
                  <TableHead>User Details</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Last Sign In</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody className="divide-y divide-slate-100 text-xs">
                {paginatedUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-10 text-slate-500">
                      No users found matching your criteria.
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedUsers.map((user) => {
                    const theme = getCardTheme(user.role);
                    const isSelected = selectedUserIds.includes(user.id);

                    return (
                      <TableRow
                        key={user.id}
                        className={`hover:bg-purple-50/20 transition-colors ${
                          isSelected ? "bg-purple-50/30" : ""
                        }`}
                      >
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handleToggleSelectUser(user.id)}
                            className="w-3.5 h-3.5 rounded border-slate-300 text-purple-600 focus:ring-purple-500"
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${theme.avatarBg}`}
                            >
                              {getUserInitials(user)}
                            </div>
                            <div>
                              <div className="font-bold text-slate-900">
                                {`${user.first_name || ""} ${user.last_name || ""}`.trim() || "Unnamed User"}
                              </div>
                              <div className="text-slate-500 text-xs font-mono">{user.email}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{renderRoleBadge(user.role)}</TableCell>
                        <TableCell>{renderStatusBadge(user.status)}</TableCell>
                        <TableCell className="font-mono text-slate-600">
                          {user.created_at ? new Date(user.created_at).toLocaleDateString() : "-"}
                        </TableCell>
                        <TableCell className="font-mono text-slate-600">
                          {user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString() : "Never"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                navigate(`${ROUTES.ADMIN_USERS_VIEW?.path || "/admin/users/view"}?id=${user.id}`)
                              }
                              className="h-7 px-2 text-xs font-semibold shadow-2xs"
                            >
                              <Eye size={13} className="mr-1 text-slate-500" />
                              View
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                navigate(`${ROUTES.ADMIN_USERS_EDIT?.path || "/admin/users/edit"}?id=${user.id}`)
                              }
                              className="h-7 px-2 text-xs font-semibold shadow-2xs"
                            >
                              <Edit size={13} className="mr-1 text-slate-500" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDeleteConfirmUser(user)}
                              className="h-7 px-2 text-xs font-semibold text-rose-600 hover:text-rose-700 hover:bg-rose-50 shadow-2xs"
                            >
                              <Trash2 size={13} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {/* 3. Pagination Footer */}
        <div className="p-3.5 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-600">
          <div className="flex items-center gap-2">
            <span>Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="h-8 px-2 rounded-lg border border-slate-300 bg-white font-medium text-xs focus:ring-2 focus:ring-purple-600"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span className="text-slate-400">|</span>
            <span className="font-mono">
              {filteredUsers.length === 0
                ? "0 records"
                : `${(currentPage - 1) * pageSize + 1}–${Math.min(
                    currentPage * pageSize,
                    filteredUsers.length
                  )} of ${filteredUsers.length} records`}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              className="h-8 px-3 text-xs font-semibold shadow-2xs"
            >
              Previous
            </Button>
            <div className="px-2 text-xs font-mono font-semibold text-slate-700">
              {currentPage} / {totalPages}
            </div>
            <Button
              variant="outline"
              size="sm"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              className="h-8 px-3 text-xs font-semibold shadow-2xs"
            >
              Next
            </Button>
          </div>
        </div>

      </div>

      {/* Accessible Radix AlertDialog for Deletion Confirmation */}
      <AlertDialog open={deleteConfirmUser !== null} onOpenChange={(open) => !open && setDeleteConfirmUser(null)}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-base font-bold text-slate-900 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-rose-600" />
              Delete User Account
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs text-slate-600 mt-2">
              Are you sure you want to delete{" "}
              <strong className="text-slate-900">
                {deleteConfirmUser
                  ? `${deleteConfirmUser.first_name || ""} ${deleteConfirmUser.last_name || ""}`.trim() ||
                    deleteConfirmUser.email
                  : "this user"}
              </strong>
              ? This action permanently removes the user's login credentials, batch enrollments, and access permissions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel className="h-8 text-xs font-semibold">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleExecuteDelete}
              disabled={deleteUserMutation.isPending}
              className="h-8 text-xs font-semibold bg-rose-600 hover:bg-rose-700 text-white"
            >
              {deleteUserMutation.isPending ? "Deleting..." : "Confirm Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default ListUsers;
